"use client";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";

import { ReduxProvider } from "@/ReduxProvider";
import { theme } from "@/styles/theme";
import { unlockAudioContext } from "@/utils/notificationSound";
import { setupPreloadErrorReload } from "@/utils/reloadOnPreloadError";

import { PostHogPageView } from "./PostHogPageView";
import { PostHogProvider } from "./PostHogProvider";
import { SentryProvider } from "./SentryProvider";

type Props = {
    children: ReactNode;
};

export const AppProviders = ({ children }: Props) => {
    const [emotionCache] = useState(() => {
        if (typeof document === "undefined") {
            return createCache({ key: "mui", prepend: true });
        }
        const insertionPoint = document.querySelector<HTMLMetaElement>(
            'meta[name="emotion-insertion-point"]',
        );
        return createCache({
            key: "mui",
            insertionPoint: insertionPoint ?? undefined,
        });
    });
    useEffect(() => {
        // Keep listener active (not one-time) so iOS AudioContext is re-unlocked
        // after every tap. iOS suspends AudioContext after audio inactivity, so
        // any subsequent tap must re-activate it.
        const unlock = () => unlockAudioContext();
        document.addEventListener("click", unlock);
        document.addEventListener("touchstart", unlock);
        return () => {
            document.removeEventListener("click", unlock);
            document.removeEventListener("touchstart", unlock);
        };
    }, []);

    // Stale clients can fail to load chunks after a new deployment — reload to recover.
    useEffect(() => setupPreloadErrorReload(), []);

    const [queryClient] = useState(
        () =>
            new QueryClient({
                queryCache: new QueryCache({
                    onError: (error) => Sentry.captureException(error),
                }),
                mutationCache: new MutationCache({
                    onError: (error) => Sentry.captureException(error),
                }),
            }),
    );

    return (
        <SentryProvider>
            <PostHogProvider>
                <CacheProvider value={emotionCache}>
                    <QueryClientProvider client={queryClient}>
                        <ThemeProvider theme={theme}>
                            <CssBaseline />
                            <PostHogPageView />
                            <ReduxProvider>{children}</ReduxProvider>
                        </ThemeProvider>
                    </QueryClientProvider>
                </CacheProvider>
            </PostHogProvider>
        </SentryProvider>
    );
};
